import { FC } from 'react'
import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material'
import {
  AVISO_ELEGIBILIDADE_NAO_E_APROVACAO,
  AvaliacaoElegibilidade,
  ROTULO_RESULTADO_ELEGIBILIDADE,
  ResultadoElegibilidade,
  TEXTO_ALERTA_ELEGIBILIDADE_RESERVA,
  TOOLTIP_AVALIACAO_ORIGINEI,
  TipoElegibilidade,
} from '@/types/elegibilidade'
import { ChipRotaOperacao } from '@/components/elegibilidade/ChipElegibilidade'
import { fracaoComoPercentual, textoPolitica } from '@/components/elegibilidade/formatoElegibilidade'

const SEVERIDADE: Record<ResultadoElegibilidade, 'success' | 'info' | 'warning'> = {
  ATENDE: 'success',
  AVALIACAO_ORIGINEI: 'info',
  NAO_ATENDE: 'warning',
  DADOS_INSUFICIENTES: 'warning',
  SEM_POLITICA: 'warning',
}

export interface AlertaElegibilidadeProps {
  avaliacao: AvaliacaoElegibilidade & {
    politicaVersao?: number | null
    politicaTipo?: TipoElegibilidade | null
    politicaVigenciaInicio?: string | null
  }
  /**
   * `reserva` (padrão): rodapé da Nova Reserva ("o alerta não impede o cadastro; impede o
   * encaminhamento…"). `generico`: só "Elegibilidade não equivale a aprovação de crédito."
   */
  contexto?: 'reserva' | 'generico'
  sx?: object
}

/**
 * P2 (AN-PP2 §8) — painel de elegibilidade com as explicações do motor, a versão da política e
 * o texto fixo de que elegibilidade não é aprovação. Mostra o que o servidor devolveu; nada
 * aqui decide. Nunca é bloqueio: o impedimento existe só no Encaminhamento ao parceiro (Q66).
 */
export const AlertaElegibilidade: FC<AlertaElegibilidadeProps> = ({ avaliacao, contexto = 'reserva', sx }) => {
  const politica = textoPolitica(avaliacao)
  const explicacoes = avaliacao.explicacoes ?? []
  const consequencias = Array.from(
    new Set(explicacoes.map((e) => e.consequencia).filter((c): c is string => !!c))
  )
  return (
    <Alert
      severity={SEVERIDADE[avaliacao.resultado] ?? 'info'}
      variant="outlined"
      sx={sx}
      data-testid="alerta-elegibilidade"
      aria-label="Elegibilidade"
    >
      <AlertTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>
            {avaliacao.resultado === 'ATENDE' || avaliacao.resultado === 'AVALIACAO_ORIGINEI'
              ? ROTULO_RESULTADO_ELEGIBILIDADE[avaliacao.resultado]
              : `Alerta de elegibilidade — ${ROTULO_RESULTADO_ELEGIBILIDADE[avaliacao.resultado] ?? avaliacao.resultado}`}
          </span>
          <ChipRotaOperacao rota={avaliacao.rota} />
        </Stack>
      </AlertTitle>
      {avaliacao.resultado === 'AVALIACAO_ORIGINEI' && explicacoes.length === 0 && (
        <Typography variant="body2">{TOOLTIP_AVALIACAO_ORIGINEI}</Typography>
      )}
      {explicacoes.map((e, i) => (
        <Typography key={`${e.codigo}-${i}`} variant="body2">
          • {e.mensagem}
        </Typography>
      ))}
      {avaliacao.ltvCalculado != null && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          LTV (calculado pelo servidor): {fracaoComoPercentual(avaliacao.ltvCalculado)}
        </Typography>
      )}
      {consequencias.map((c) => (
        <Typography key={c} variant="caption" component="div" sx={{ mt: 0.5 }}>
          {c}
        </Typography>
      ))}
      {politica && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {politica}
        </Typography>
      )}
      <Box sx={{ mt: 0.75 }}>
        <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
          {contexto === 'reserva' ? TEXTO_ALERTA_ELEGIBILIDADE_RESERVA : AVISO_ELEGIBILIDADE_NAO_E_APROVACAO}
        </Typography>
      </Box>
    </Alert>
  )
}

export default AlertaElegibilidade
